import React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';
import { FormField } from '@packages/ui/patterns/FormField';
import { Typography } from '@packages/ui/primitives';
import { rhfInlineError } from '@packages/ui/hook-form-adapters/rhfInlineError';

interface BaseAdapterProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  semanticId?: string;
  disabled?: boolean;
}

/** Read-only prose display adapter. */
export function RhfProseField({
  control,
  name,
  label,
  hint,
  required,
  semanticId,
}: BaseAdapterProps): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormField label={label} hint={hint} required={required} semanticId={semanticId ?? name}>
          <Typography variant='body'>{field.value != null ? String(field.value) : '-'}</Typography>
        </FormField>
      )}
    />
  );
}

/** Display-only label adapter (no input). */
export function RhfLabelField({
  control,
  name,
  label,
  semanticId,
}: BaseAdapterProps): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormField label={label} semanticId={semanticId ?? name}>
          <Typography variant='body-s'>{field.value != null ? String(field.value) : label}</Typography>
        </FormField>
      )}
    />
  );
}

function PlaceholderAdapter({
  control,
  name,
  label,
  hint,
  required,
  semanticId,
  adapterName,
}: BaseAdapterProps & { adapterName: string }): React.ReactElement {
  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState, formState }) => {
        const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
        return (
          <FormField
            label={label}
            hint={hint ?? `${adapterName} adapter — register a custom implementation to replace this placeholder.`}
            required={required}
            semanticId={semanticId ?? name}
            error={errorText}
          >
            <Typography variant='body-s' color='var(--ui-text-muted)'>
              [{adapterName}] placeholder
            </Typography>
          </FormField>
        );
      }}
    />
  );
}

export function RhfMonacoField(props: BaseAdapterProps): React.ReactElement {
  return <PlaceholderAdapter {...props} adapterName='monaco' />;
}

export function RhfJsonField(props: BaseAdapterProps): React.ReactElement {
  return <PlaceholderAdapter {...props} adapterName='json' />;
}

export function RhfMultiSelectPanel(props: BaseAdapterProps): React.ReactElement {
  return <PlaceholderAdapter {...props} adapterName='multi-select-panel' />;
}
